import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

type Lang = "es" | "en";

interface Segment {
  id: string;
  name: string;
  description: string | null;
  definition: any;
  is_active: boolean;
  updated_at: string;
}

interface Flow {
  id: string;
  name: string;
  description: string | null;
  trigger_event: string;
  segment_id: string | null;
  steps: any[];
  is_active: boolean;
}

interface AbRow {
  flow_id: string;
  step_index: number;
  variant: string;
  sent_count: number;
  open_count: number;
  click_count: number;
  conversion_count: number;
}

const TRIGGER_OPTS = [
  { v: "signup", es: "Registro", en: "Signup" },
  { v: "abandoned_cart", es: "Carrito abandonado", en: "Abandoned cart" },
  { v: "post_purchase", es: "Post-compra", en: "Post-purchase" },
  { v: "tier_upgrade", es: "Subida de tier", en: "Tier upgrade" },
  { v: "win_back", es: "Recuperación", en: "Win-back" },
  { v: "manual", es: "Manual", en: "Manual" },
];

const TIER_OPTS = ["bronze", "silver", "gold", "platinum"];

export function AdminMarketingAutomation({ lang, toast }: { lang: Lang; toast: (m: string) => void }) {
  const [tab, setTab] = useState<"segments" | "flows" | "ab">("segments");

  return (
    <div className="ag">
      <div className="atabs" style={{ marginBottom: 14 }}>
        {(["segments", "flows", "ab"] as const).map(t => (
          <button key={t} className={`atab ${tab === t ? "on" : ""}`} onClick={() => setTab(t)}>
            {t === "segments" ? (lang === "es" ? "Segmentos" : "Segments")
              : t === "flows" ? (lang === "es" ? "Flujos" : "Flows")
              : (lang === "es" ? "A/B asuntos" : "A/B subjects")}
          </button>
        ))}
      </div>

      {tab === "segments" && <SegmentsTab lang={lang} toast={toast} />}
      {tab === "flows" && <FlowsTab lang={lang} toast={toast} />}
      {tab === "ab" && <AbResultsTab lang={lang} />}
    </div>
  );
}

/* ───────── Segments ───────── */
function SegmentsTab({ lang, toast }: { lang: Lang; toast: (m: string) => void }) {
  const [items, setItems] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Segment | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("marketing_segments")
      .select("*")
      .order("updated_at", { ascending: false });
    if (error) toast(error.message);
    setItems((data ?? []) as Segment[]);
    setLoading(false);
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const newSegment = () => setEditing({
    id: "",
    name: "",
    description: "",
    definition: { tier: [], lifetime_spend_min: null, last_order_days_max: null, language: null },
    is_active: true,
    updated_at: "",
  });

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h3 style={{ margin: 0 }}>{lang === "es" ? "Segmentos de clientes" : "Customer segments"}</h3>
        <button className="btn bp" onClick={newSegment}>+ {lang === "es" ? "Nuevo" : "New"}</button>
      </div>
      {loading ? <p style={{ color: "var(--tx3)" }}>…</p> : items.length === 0 ? (
        <p style={{ color: "var(--tx3)" }}>{lang === "es" ? "Sin segmentos." : "No segments yet."}</p>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {items.map(s => (
            <div key={s.id} style={{ padding: 12, border: "1px solid var(--bd)", borderRadius: 10, background: "var(--bg2)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 700, display: "flex", gap: 8, alignItems: "center" }}>
                  {s.name}
                  {!s.is_active && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 999, background: "var(--bg3)", color: "var(--tx2)" }}>{lang === "es" ? "Pausado" : "Paused"}</span>}
                </div>
                <div style={{ fontSize: 11, color: "var(--tx3)", marginTop: 2 }}>{summarizeDefinition(s.definition, lang)}</div>
                {s.description && <div style={{ fontSize: 11, color: "var(--tx2)", marginTop: 4 }}>{s.description}</div>}
              </div>
              <button className="btn" onClick={() => setEditing(s)}>{lang === "es" ? "Editar" : "Edit"}</button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <SegmentEditor
          lang={lang}
          toast={toast}
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}
    </>
  );
}

function summarizeDefinition(def: any, lang: Lang): string {
  const parts: string[] = [];
  if (def?.tier?.length) parts.push(`tier: ${def.tier.join(", ")}`);
  if (def?.lifetime_spend_min) parts.push(`${lang === "es" ? "gasto≥" : "spend≥"}${def.lifetime_spend_min}`);
  if (def?.last_order_days_max) parts.push(`${lang === "es" ? "compró últ." : "ordered ≤"} ${def.last_order_days_max}d`);
  if (def?.last_order_days_min) parts.push(`${lang === "es" ? "inactivo ≥" : "inactive ≥"} ${def.last_order_days_min}d`);
  if (def?.language) parts.push(`lang: ${def.language}`);
  return parts.length ? parts.join(" • ") : (lang === "es" ? "Todos los clientes" : "All customers");
}

function SegmentEditor({ item, lang, toast, onClose, onSaved }: {
  item: Segment; lang: Lang; toast: (m: string) => void; onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description ?? "");
  const [tier, setTier] = useState<string[]>(item.definition?.tier ?? []);
  const [spendMin, setSpendMin] = useState<string>(item.definition?.lifetime_spend_min?.toString() ?? "");
  const [orderedMaxD, setOrderedMaxD] = useState<string>(item.definition?.last_order_days_max?.toString() ?? "");
  const [inactiveMinD, setInactiveMinD] = useState<string>(item.definition?.last_order_days_min?.toString() ?? "");
  const [language, setLanguage] = useState<string>(item.definition?.language ?? "");
  const [isActive, setIsActive] = useState(item.is_active);
  const [preview, setPreview] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const buildDef = () => ({
    tier: tier.length ? tier : null,
    lifetime_spend_min: spendMin ? Number(spendMin) : null,
    last_order_days_max: orderedMaxD ? Number(orderedMaxD) : null,
    last_order_days_min: inactiveMinD ? Number(inactiveMinD) : null,
    language: language || null,
  });

  const runPreview = async () => {
    setBusy(true);
    const { data, error } = await supabase.rpc("preview_marketing_segment", { _definition: buildDef() });
    setBusy(false);
    if (error) { toast(error.message); return; }
    const count = Array.isArray(data) ? Number((data[0] as any)?.matched_count ?? 0) : Number((data as any)?.matched_count ?? 0);
    setPreview(count);
  };

  const save = async () => {
    if (!name.trim()) { toast(lang === "es" ? "Nombre requerido" : "Name required"); return; }
    setBusy(true);
    const payload = { name: name.trim(), description: description || null, definition: buildDef(), is_active: isActive };
    const { error } = item.id
      ? await supabase.from("marketing_segments").update(payload).eq("id", item.id)
      : await supabase.from("marketing_segments").insert(payload);
    setBusy(false);
    if (error) { toast(error.message); return; }
    toast(lang === "es" ? "Segmento guardado" : "Segment saved");
    onSaved();
  };

  const remove = async () => {
    if (!item.id) { onClose(); return; }
    if (!confirm(lang === "es" ? "¿Eliminar segmento?" : "Delete segment?")) return;
    const { error } = await supabase.from("marketing_segments").delete().eq("id", item.id);
    if (error) { toast(error.message); return; }
    onSaved();
  };

  return (
    <div className="modal" onClick={onClose}>
      <div className="mcard" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <h3 style={{ marginTop: 0 }}>{item.id ? (lang === "es" ? "Editar segmento" : "Edit segment") : (lang === "es" ? "Nuevo segmento" : "New segment")}</h3>

        <label className="fl">{lang === "es" ? "Nombre" : "Name"}</label>
        <input className="inp" value={name} onChange={e => setName(e.target.value)} />

        <label className="fl" style={{ marginTop: 10 }}>{lang === "es" ? "Descripción" : "Description"}</label>
        <textarea className="inp" rows={2} value={description} onChange={e => setDescription(e.target.value)} />

        <div style={{ marginTop: 12, padding: 10, border: "1px solid var(--bd)", borderRadius: 8, background: "var(--bg2)" }}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8 }}>{lang === "es" ? "Criterios" : "Criteria"}</div>

          <div style={{ fontSize: 11, marginBottom: 4 }}>{lang === "es" ? "Tiers de lealtad" : "Loyalty tiers"}</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {TIER_OPTS.map(t => {
              const on = tier.includes(t);
              return (
                <button key={t} type="button"
                  onClick={() => setTier(on ? tier.filter(x => x !== t) : [...tier, t])}
                  style={{ padding: "4px 10px", borderRadius: 999, border: "1px solid var(--bd)", background: on ? "var(--ac)" : "var(--bg)", color: on ? "#fff" : "var(--tx)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                  {t}
                </button>
              );
            })}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <label className="fl" style={{ fontSize: 11 }}>{lang === "es" ? "Gasto mínimo (RD$)" : "Min lifetime spend"}</label>
              <input className="inp" type="number" value={spendMin} onChange={e => setSpendMin(e.target.value)} />
            </div>
            <div>
              <label className="fl" style={{ fontSize: 11 }}>{lang === "es" ? "Compra últimos (días)" : "Ordered within (days)"}</label>
              <input className="inp" type="number" value={orderedMaxD} onChange={e => setOrderedMaxD(e.target.value)} />
            </div>
            <div>
              <label className="fl" style={{ fontSize: 11 }}>{lang === "es" ? "Inactivo ≥ (días)" : "Inactive ≥ (days)"}</label>
              <input className="inp" type="number" value={inactiveMinD} onChange={e => setInactiveMinD(e.target.value)} />
            </div>
            <div>
              <label className="fl" style={{ fontSize: 11 }}>{lang === "es" ? "Idioma" : "Language"}</label>
              <select className="inp" value={language} onChange={e => setLanguage(e.target.value)}>
                <option value="">{lang === "es" ? "Cualquiera" : "Any"}</option>
                <option value="es">es</option>
                <option value="en">en</option>
              </select>
            </div>
          </div>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12, fontSize: 12 }}>
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
          {lang === "es" ? "Activo" : "Active"}
        </label>

        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <button className="btn" disabled={busy} onClick={runPreview}>{lang === "es" ? "Previsualizar tamaño" : "Preview size"}</button>
          {preview !== null && (
            <span style={{ fontSize: 12, color: "var(--tx2)" }}>
              <b>{preview.toLocaleString()}</b> {lang === "es" ? "clientes coinciden" : "customers match"}
            </span>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "space-between" }}>
          <button className="btn" onClick={remove} style={{ color: "var(--er)" }}>{item.id ? (lang === "es" ? "Eliminar" : "Delete") : (lang === "es" ? "Cancelar" : "Cancel")}</button>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" onClick={onClose}>{lang === "es" ? "Cerrar" : "Close"}</button>
            <button className="btn bp" disabled={busy} onClick={save}>{lang === "es" ? "Guardar" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────── Flows ───────── */
function FlowsTab({ lang, toast }: { lang: Lang; toast: (m: string) => void }) {
  const [items, setItems] = useState<Flow[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Flow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: f, error: e1 }, { data: s }] = await Promise.all([
      supabase.from("marketing_flows").select("*").order("updated_at", { ascending: false }),
      supabase.from("marketing_segments").select("*").eq("is_active", true),
    ]);
    if (e1) toast(e1.message);
    setItems((f ?? []) as Flow[]);
    setSegments((s ?? []) as Segment[]);
    setLoading(false);
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const newFlow = () => setEditing({
    id: "",
    name: "",
    description: "",
    trigger_event: "signup",
    segment_id: null,
    steps: [{ delay_hours: 0, channel: "email", template: "welcome", subject_variants: [""] }],
    is_active: true,
  });

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h3 style={{ margin: 0 }}>{lang === "es" ? "Flujos automáticos" : "Automated flows"}</h3>
        <button className="btn bp" onClick={newFlow}>+ {lang === "es" ? "Nuevo" : "New"}</button>
      </div>
      {loading ? <p style={{ color: "var(--tx3)" }}>…</p> : items.length === 0 ? (
        <p style={{ color: "var(--tx3)" }}>{lang === "es" ? "Sin flujos todavía." : "No flows yet."}</p>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {items.map(f => (
            <div key={f.id} style={{ padding: 12, border: "1px solid var(--bd)", borderRadius: 10, background: "var(--bg2)", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 700, display: "flex", gap: 8, alignItems: "center" }}>
                  {f.name}
                  <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 999, background: "var(--ac)", color: "#fff" }}>
                    {TRIGGER_OPTS.find(o => o.v === f.trigger_event)?.[lang] ?? f.trigger_event}
                  </span>
                  {!f.is_active && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 999, background: "var(--bg3)", color: "var(--tx2)" }}>{lang === "es" ? "Pausado" : "Paused"}</span>}
                </div>
                <div style={{ fontSize: 11, color: "var(--tx3)", marginTop: 2 }}>
                  {(f.steps?.length ?? 0)} {lang === "es" ? "pasos" : "steps"}
                  {f.segment_id && segments.find(s => s.id === f.segment_id) && ` • ${segments.find(s => s.id === f.segment_id)!.name}`}
                </div>
              </div>
              <button className="btn" onClick={() => setEditing(f)}>{lang === "es" ? "Editar" : "Edit"}</button>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <FlowEditor lang={lang} toast={toast} item={editing} segments={segments}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }} />
      )}
    </>
  );
}

function FlowEditor({ item, segments, lang, toast, onClose, onSaved }: {
  item: Flow; segments: Segment[]; lang: Lang; toast: (m: string) => void; onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description ?? "");
  const [trigger, setTrigger] = useState(item.trigger_event);
  const [segmentId, setSegmentId] = useState<string>(item.segment_id ?? "");
  const [steps, setSteps] = useState<any[]>(Array.isArray(item.steps) ? item.steps : []);
  const [isActive, setIsActive] = useState(item.is_active);
  const [busy, setBusy] = useState(false);

  const updateStep = (i: number, patch: any) =>
    setSteps(steps.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const addStep = () => setSteps([...steps, { delay_hours: 24, channel: "email", template: "", subject_variants: [""] }]);
  const removeStep = (i: number) => setSteps(steps.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) { toast(lang === "es" ? "Nombre requerido" : "Name required"); return; }
    setBusy(true);
    const payload = {
      name: name.trim(),
      description: description || null,
      trigger_event: trigger as any,
      segment_id: segmentId || null,
      steps,
      is_active: isActive,
    };
    const { error } = item.id
      ? await supabase.from("marketing_flows").update(payload).eq("id", item.id)
      : await supabase.from("marketing_flows").insert(payload);
    setBusy(false);
    if (error) { toast(error.message); return; }
    toast(lang === "es" ? "Flujo guardado" : "Flow saved");
    onSaved();
  };

  const remove = async () => {
    if (!item.id) { onClose(); return; }
    if (!confirm(lang === "es" ? "¿Eliminar flujo?" : "Delete flow?")) return;
    const { error } = await supabase.from("marketing_flows").delete().eq("id", item.id);
    if (error) { toast(error.message); return; }
    onSaved();
  };

  return (
    <div className="modal" onClick={onClose}>
      <div className="mcard" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <h3 style={{ marginTop: 0 }}>{item.id ? (lang === "es" ? "Editar flujo" : "Edit flow") : (lang === "es" ? "Nuevo flujo" : "New flow")}</h3>

        <label className="fl">{lang === "es" ? "Nombre" : "Name"}</label>
        <input className="inp" value={name} onChange={e => setName(e.target.value)} />

        <label className="fl" style={{ marginTop: 10 }}>{lang === "es" ? "Descripción" : "Description"}</label>
        <textarea className="inp" rows={2} value={description} onChange={e => setDescription(e.target.value)} />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
          <div>
            <label className="fl">{lang === "es" ? "Disparador" : "Trigger"}</label>
            <select className="inp" value={trigger} onChange={e => setTrigger(e.target.value as any)}>
              {TRIGGER_OPTS.map(o => <option key={o.v} value={o.v}>{o[lang]}</option>)}
            </select>
          </div>
          <div>
            <label className="fl">{lang === "es" ? "Segmento" : "Segment"}</label>
            <select className="inp" value={segmentId} onChange={e => setSegmentId(e.target.value)}>
              <option value="">{lang === "es" ? "— Cualquiera —" : "— Any —"}</option>
              {segments.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginTop: 14, fontWeight: 700, fontSize: 12 }}>{lang === "es" ? "Pasos" : "Steps"}</div>
        <div style={{ display: "grid", gap: 8, marginTop: 6 }}>
          {steps.map((s, i) => (
            <div key={i} style={{ border: "1px solid var(--bd)", borderRadius: 8, padding: 10, background: "var(--bg2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <b style={{ fontSize: 12 }}>{lang === "es" ? "Paso" : "Step"} {i + 1}</b>
                <button className="btn" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => removeStep(i)}>{lang === "es" ? "Quitar" : "Remove"}</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                <div>
                  <label className="fl" style={{ fontSize: 10 }}>{lang === "es" ? "Retraso (h)" : "Delay (h)"}</label>
                  <input className="inp" type="number" value={s.delay_hours ?? 0} onChange={e => updateStep(i, { delay_hours: Number(e.target.value) })} />
                </div>
                <div>
                  <label className="fl" style={{ fontSize: 10 }}>{lang === "es" ? "Canal" : "Channel"}</label>
                  <select className="inp" value={s.channel ?? "email"} onChange={e => updateStep(i, { channel: e.target.value })}>
                    <option value="email">email</option>
                    <option value="push">push</option>
                  </select>
                </div>
                <div>
                  <label className="fl" style={{ fontSize: 10 }}>{lang === "es" ? "Plantilla" : "Template"}</label>
                  <input className="inp" value={s.template ?? ""} onChange={e => updateStep(i, { template: e.target.value })} />
                </div>
              </div>
              <div style={{ marginTop: 8 }}>
                <label className="fl" style={{ fontSize: 10 }}>{lang === "es" ? "Variantes A/B del asunto (una por línea)" : "A/B subject variants (one per line)"}</label>
                <textarea
                  className="inp"
                  rows={2}
                  value={(s.subject_variants ?? []).join("\n")}
                  onChange={e => updateStep(i, { subject_variants: e.target.value.split("\n").map((x: string) => x).filter((_: any, idx: number, arr: any[]) => idx < arr.length || true) })}
                  placeholder={lang === "es" ? "Bienvenido a NutriMart\n¡Hola! Te esperábamos" : "Welcome to NutriMart\nHey, we’ve been waiting"}
                />
                <div style={{ fontSize: 10, color: "var(--tx3)", marginTop: 2 }}>
                  {(s.subject_variants ?? []).filter((x: string) => x.trim()).length} {lang === "es" ? "variantes" : "variants"}
                </div>
              </div>
            </div>
          ))}
          <button className="btn" onClick={addStep}>+ {lang === "es" ? "Agregar paso" : "Add step"}</button>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12, fontSize: 12 }}>
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
          {lang === "es" ? "Activo" : "Active"}
        </label>

        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "space-between" }}>
          <button className="btn" onClick={remove} style={{ color: "var(--er)" }}>{item.id ? (lang === "es" ? "Eliminar" : "Delete") : (lang === "es" ? "Cancelar" : "Cancel")}</button>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" onClick={onClose}>{lang === "es" ? "Cerrar" : "Close"}</button>
            <button className="btn bp" disabled={busy} onClick={save}>{lang === "es" ? "Guardar" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────── A/B Results ───────── */
function AbResultsTab({ lang }: { lang: Lang }) {
  const [rows, setRows] = useState<AbRow[]>([]);
  const [flows, setFlows] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [{ data: r }, { data: f }] = await Promise.all([
        supabase.from("marketing_ab_results").select("*").order("updated_at", { ascending: false }).limit(200),
        supabase.from("marketing_flows").select("id,name"),
      ]);
      setRows((r ?? []) as AbRow[]);
      const map: Record<string, string> = {};
      (f ?? []).forEach((x: any) => { map[x.id] = x.name; });
      setFlows(map);
      setLoading(false);
    })();
  }, []);

  if (loading) return <p style={{ color: "var(--tx3)" }}>…</p>;
  if (rows.length === 0) return (
    <div style={{ padding: 18, border: "1px dashed var(--bd)", borderRadius: 10, color: "var(--tx3)", fontSize: 13 }}>
      {lang === "es"
        ? "Sin resultados todavía. Los datos se llenarán al ejecutarse los flujos."
        : "No results yet. Data will populate as flows run."}
    </div>
  );

  // Group by flow + step
  const groups: Record<string, AbRow[]> = {};
  rows.forEach(r => {
    const k = `${r.flow_id}::${r.step_index}`;
    (groups[k] ||= []).push(r);
  });

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {Object.entries(groups).map(([key, variants]) => {
        const [fid, stepIdx] = key.split("::");
        const totalSent = variants.reduce((a, v) => a + v.sent_count, 0) || 1;
        return (
          <div key={key} style={{ border: "1px solid var(--bd)", borderRadius: 10, padding: 12, background: "var(--bg2)" }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>
              {flows[fid] ?? fid.slice(0, 8)} · {lang === "es" ? "Paso" : "Step"} {Number(stepIdx) + 1}
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              {variants.map(v => {
                const openRate = v.sent_count ? (v.open_count / v.sent_count) * 100 : 0;
                const clickRate = v.sent_count ? (v.click_count / v.sent_count) * 100 : 0;
                const share = (v.sent_count / totalSent) * 100;
                return (
                  <div key={v.variant} style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 10, alignItems: "center", fontSize: 12 }}>
                    <div title={v.variant} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.variant}</div>
                    <div style={{ color: "var(--tx2)" }}>{v.sent_count} ({share.toFixed(0)}%)</div>
                    <div style={{ color: "var(--ac)" }}>open {openRate.toFixed(1)}%</div>
                    <div style={{ color: "var(--ac3)" }}>click {clickRate.toFixed(1)}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}